import { LinearProgress } from '@mui/joy';

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Error as ErrorComponent } from '@/components/Hub/Error';
import { RepoForm, useRepoState } from '@/components/Hub/RepoForm';
import { usePromptNameDuplicateCheck } from '@/hooks/usePromptNameDuplicateCheck';
import {
  useForkRepoMutation,
  useOrganizationId,
  useRepo,
  useSelectedTenant,
} from '@/hooks/useSwr';
import { useCreateTagsOnResource } from '@/hooks/useTagsOnResource';
import { ResourceType } from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';

export function HubForkRepo({
  inHub,
  hideTitle,
  onSuccess,
}: {
  inHub: boolean;
  hideTitle?: boolean;
  onSuccess?: () => void;
}) {
  const { owner, repo: repoHandle } = useParams();
  const {
    data: repoObj,
    isLoading,
    error: repoError,
  } = useRepo(owner, repoHandle!);
  const { data: selectedTenant } = useSelectedTenant();
  const organizationId = useOrganizationId();
  const [repo, setRepo] = useRepoState();

  const {
    trigger: createTagsOnResource,
    tagsOnResource,
    setTagsOnResource,
  } = useCreateTagsOnResource(ResourceType.Prompt);

  const {
    hasDuplicate: hasPromptWithSameName,
    isValidating: isValidatingPrompts,
  } = usePromptNameDuplicateCheck(repo.repo_handle);

  useEffect(() => {
    if (
      repoObj?.repo &&
      selectedTenant?.tenant_handle &&
      !repo.description &&
      !repo.readme &&
      !repo.tags.length
    ) {
      setRepo({
        owner: selectedTenant.tenant_handle,
        repo_handle: `${repoObj.repo.repo_handle}-fork`,
        description: repoObj.repo.description ?? '',
        readme: repoObj.repo.readme ?? '',
        is_public: false,
        tags: repoObj.repo.tags,
      });
    }
  }, [repoObj]);

  const {
    trigger: forkRepo,
    error: forkMutationError,
    isMutating: isMutatingFork,
  } = useForkRepoMutation(owner, repoHandle!);
  const navigate = useNavigate();
  const onSubmit = async () => {
    const res = await forkRepo({
      json: repo,
    });
    if (res !== undefined) {
      onSuccess?.();

      if (tagsOnResource && tagsOnResource.length > 0 && !res.repo.is_public) {
        await createTagsOnResource(res.repo.id);
      }

      const newUrl = addQueryParams(
        inHub && res.repo.is_public
          ? `/${appHubIndexPath}/${res.repo.full_name}`
          : `/${appPromptsIndexPath}/${res.repo.repo_handle}`,
        { organizationId }
      );
      navigate(newUrl);
    }
  };

  const getError = () => {
    if (hasPromptWithSameName) {
      return new Error('A prompt with this name already exists');
    }
    return forkMutationError;
  };

  if (isLoading && !repoObj) return <LinearProgress />;
  if (repoError) return <ErrorComponent>{repoError.message}</ErrorComponent>;
  return (
    <RepoForm
      repo={repo}
      setRepo={setRepo}
      isValidatingPrompts={isValidatingPrompts}
      tenantHandle={owner}
      mode="fork"
      onSubmit={onSubmit}
      error={getError()}
      tagsOnResource={tagsOnResource}
      setTagsOnResource={setTagsOnResource}
      hideTitle={hideTitle}
      isLoading={isMutatingFork}
    />
  );
}
